import { CodeHighlighting } from '@w/code-highlighting'
import { CodeXmlIcon } from '@w/ui/components/icon'
import { IconButton } from '@w/ui/components/icon-button'
import { cn } from '@w/ui/lib/utils'
import type {
  StoryDocsExample,
  StoryDocsRenderer as StoryDocsRendererComponent,
} from '@w/ui/storybook'
import { _dedent } from '@w/utils'
import { Fragment, useState } from 'react'
import { BlockViewer, type BlockViewerLinkProps } from './block-viewer'
import { mdToBlocks } from './parser'

const StoryDocsLink = ({ href, ...props }: BlockViewerLinkProps) => {
  if (href.startsWith('?')) {
    return <a {...props} href={href} rel={undefined} target={undefined} />
  }

  return <a {...props} href={href} />
}

export const StoryDocsRenderer: StoryDocsRendererComponent = ({ docs }) => {
  return (
    <div className="content-docs-typography flex min-w-0 flex-col gap-10">
      {docs.markdown.map((markdown, index) => {
        const normalizedMarkdown = _dedent(markdown).trim()
        const liveExample = docs.examples[index]

        return (
          <Fragment key={index}>
            {normalizedMarkdown && (
              <BlockViewer
                spaceId="storybook"
                blocks={mdToBlocks(normalizedMarkdown)}
                className="max-w-none"
                Link={StoryDocsLink}
              />
            )}
            {liveExample && <StoryDocsLiveExample example={liveExample} />}
          </Fragment>
        )
      })}
    </div>
  )
}

function StoryDocsLiveExample({ example }: { example: StoryDocsExample }) {
  const [codeVisible, setCodeVisible] = useState(false)
  const Example = example.Component

  return (
    <section
      className="group/example relative min-w-0"
      data-slot="story-docs-example"
    >
      <div className="min-w-0">
        <div
          className={cn(
            'bg-surface-2 flex min-h-44 min-w-0 flex-wrap items-center justify-center gap-6 rounded-xl p-6',
            codeVisible && example.source && 'rounded-b-none',
          )}
        >
          <Example />
        </div>
        {codeVisible && example.source && (
          <CodeHighlighting
            allowLanguageSelection={false}
            className="[&>pre]:rounded-t-none [&>pre]:rounded-b-xl"
            code={example.source}
            language="typescript"
          />
        )}
      </div>
      {example.source && (
        <IconButton
          aria-expanded={codeVisible}
          className="bg-surface-1 absolute top-0 right-0 z-10 translate-x-1/2 -translate-y-1/2 opacity-0 shadow-sm transition-opacity group-hover/example:opacity-100 [@media(hover:none)]:opacity-100"
          onClick={() => {
            setCodeVisible(!codeVisible)
          }}
          semanticRole="secondary"
          toggled={codeVisible}
          tooltipSide="left"
          variant="text"
        >
          <CodeXmlIcon />
          {codeVisible ? 'Hide code' : 'Show code'}
        </IconButton>
      )}
    </section>
  )
}
